import { Building, Plus, Users } from "lucide-react";
import {
    Badge,
    Button,
    Container,
    Dialog,
    DialogContent,
    DialogTitle,
    Heading,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
    OrgAvatar,
    PageHeader,
    PageHeaderBreadcrumb,
    PageHeaderIdentity,
    PageHeaderInfo,
    PageHeaderMeta,
    PageHeaderMetaItem,
    PageHeaderRow,
    PageHeaderTitleRow,
    SectionHeader,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@athena/ui";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@apollo/client/react";
import { CourseByIdDocument } from "../../graphql/courses.generated";
import { CourseAssignmentsDocument, CourseAssignmentsProgressDocument } from "../../graphql/assignments.generated";
import { deriveCourseStepProgress, orderByGlobalSortOrder } from "./progress";
import { AssignmentOverview } from "./AssignmentOverview";
import { AssignmentUsers } from "./AssignmentUsers";
import { AssignmentWizard } from "./AssignmentWizard";
import { useTranslation } from "react-i18next";

export function AssignmentPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const { data: courseData } = useQuery(CourseByIdDocument, { variables: { id: id ?? "" }, skip: !id });
    const { data: assignmentsData, refetch: refetchAssignments } = useQuery(CourseAssignmentsDocument, {
        variables: { courseId: id ?? "" },
        skip: !id,
    });
    const { data: progressData, refetch: refetchProgress } = useQuery(CourseAssignmentsProgressDocument, {
        variables: { courseId: id ?? "" },
        skip: !id,
    });

    const course = courseData?.course;
    const rawAssignments = assignmentsData?.courseAssignments ?? [];
    const orderedContent = orderByGlobalSortOrder(course?.content ?? []);
    const progressByUserId = new Map<string, Map<string, "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED">>();
    for (const p of progressData?.courseAssignmentsProgress ?? []) {
        const byContentId = progressByUserId.get(p.userId) ?? new Map();
        byContentId.set(p.contentId, p.status);
        progressByUserId.set(p.userId, byContentId);
    }

    const assignments = rawAssignments.map((a) => {
        /*
         * The most specific organization the learner belongs to.
         *
         * A membership in a sub-organization usually comes with one in its parent, and reading
         * `memberships[0]` picked whichever the server happened to return first — so a learner in
         * Ball State Seniors was filed under Ball State University itself, with no sub-organization
         * section to sit in and nothing on screen saying which campus they were from. The one with
         * a parent above it is the one that says where they actually are.
         */
        const membership = a.user.memberships.find((m) => m.organization?.parentOrganization) ?? a.user.memberships[0];
        const org = membership?.organization;
        const parent = org?.parentOrganization;
        const { status, currentStepIndex, totalSteps } = deriveCourseStepProgress(
            orderedContent,
            progressByUserId.get(a.user.id) ?? new Map(),
        );
        return {
            id: a.id,
            userId: a.user.id,
            name: `${a.user.firstName ?? ""} ${a.user.lastName ?? ""}`.trim(),
            email: a.user.email,
            initials: `${a.user.firstName?.[0] ?? ""}${a.user.lastName?.[0] ?? ""}`.toUpperCase(),
            organizationId: org?.id ?? null,
            organizationName: org?.name ?? "",
            // Null for a top-level organization, which is what makes it a section of its own rather
            // than one nested under something.
            parentOrganizationId: parent?.id ?? null,
            parentOrganizationName: parent?.name ?? "",
            organization: parent ? `${parent.name} — ${org?.name}` : (org?.name ?? ""),
            dueDate: a.dueDate ? new Date(a.dueDate).toLocaleDateString() : "—",
            progress: totalSteps > 0 ? `Step ${currentStepIndex + 1} of ${totalSteps}` : "—",
            status: status.toLowerCase() as "completed" | "in_progress" | "not_started",
            currentStep: totalSteps > 0 ? currentStepIndex + 1 : 0,
            totalSteps,
            assignedAt: a.assignedAt,
            completedAt: a.completedAt,
        };
    });

    const now = new Date();
    const completedCount = assignments.filter((a) => a.status === "completed").length;
    const inProgressCount = assignments.filter((a) => a.status === "in_progress").length;
    const overdueCount = rawAssignments.filter(
        (a) => a.status !== "COMPLETED" && a.dueDate != null && new Date(a.dueDate) < now,
    ).length;

    const orgStatsById = new Map<string, { id: string; name: string; total: number; completed: number }>();
    for (const a of assignments) {
        if (!a.organizationId) continue;
        const entry = orgStatsById.get(a.organizationId) ?? {
            id: a.organizationId,
            name: a.parentOrganizationName ? `${a.parentOrganizationName} (${a.organizationName})` : a.organizationName,
            total: 0,
            completed: 0,
        };
        entry.total += 1;
        if (a.status === "completed") entry.completed += 1;
        orgStatsById.set(a.organizationId, entry);
    }
    const completionByOrg = [...orgStatsById.values()].sort((a, b) => b.total - a.total);

    const [
        searchParams,
        setSearchParams,
    ] = useSearchParams();
    const [
        wizardOpen,
        setWizardOpen,
    ] = useState<boolean>(false);

    const activeTab = searchParams.get("tab") ?? "overview";

    if (!course) return null;

    return (
        <Tabs value={activeTab} onValueChange={(tab) => setSearchParams({ tab }, { replace: true })}>
            <PageHeader className="pb-0">
                <PageHeaderBreadcrumb>
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbLink href="/assignments">{t("admin.lms.assignments")}</BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem>
                                <BreadcrumbPage>{course.title}</BreadcrumbPage>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>
                </PageHeaderBreadcrumb>

                <PageHeaderRow>
                    <PageHeaderIdentity>
                        <OrgAvatar id={course.id} fallback={course.title.slice(0, 2).toUpperCase()} size="lg" />
                        <PageHeaderInfo>
                            <PageHeaderTitleRow>
                                <Heading level={3}>{course.title}</Heading>
                            </PageHeaderTitleRow>
                            <PageHeaderMeta>
                                <PageHeaderMetaItem>
                                    {assignments.length} {t("admin.lms.assigned")}
                                </PageHeaderMetaItem>
                            </PageHeaderMeta>
                        </PageHeaderInfo>
                    </PageHeaderIdentity>
                </PageHeaderRow>

                <PageHeaderRow>
                    <TabsList variant="line" className="bg-card">
                        <TabsTrigger variant="line" value="overview" className="data-[state=active]:border-secondary">
                            <Building className="text-muted-foreground mr-2 h-4 w-4" />
                            {t("common.fields.overview")}
                        </TabsTrigger>
                        <TabsTrigger variant="line" value="users" className="data-[state=active]:border-secondary">
                            <Users className="text-muted-foreground mr-2 h-4 w-4" />
                            {t("common.fields.byUser")}{" "}
                            <Badge className="bg-muted hover:bg-muted pointer-events-none mx-2 text-neutral-400">
                                {assignments.length}
                            </Badge>
                        </TabsTrigger>
                    </TabsList>
                </PageHeaderRow>
            </PageHeader>

            <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
                <DialogContent className="flex h-[90vh] max-w-7xl flex-col gap-0 p-0">
                    <DialogTitle className="border-border border-b px-6 py-4 text-base font-semibold">
                        {t("common.fields.assign")} — {course.title}
                    </DialogTitle>
                    <div className="min-h-0 flex-1">
                        <AssignmentWizard
                            courseId={course.id}
                            courseTitle={course.title}
                            courseOrganizationId={course.organizationId}
                            onClose={() => setWizardOpen(false)}
                            onAssigned={() => {
                                void refetchAssignments();
                                void refetchProgress();
                            }}
                        />
                    </div>
                </DialogContent>
            </Dialog>

            <Container maxWidth="full" className="p-6">
                <TabsContent value="overview">
                    <SectionHeader
                        title={t("admin.lms.courseOverview")}
                        description={t("admin.lms.courseOverviewDesc")}
                        actions={
                            <>
                                <Button
                                    size="sm"
                                    onClick={() => navigate(`/course-preview/${course.id}`)}
                                    aria-label="View"
                                    variant={"outline"}
                                >
                                    {t("common.actions.viewVariable", { variable: "Course" })}
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={() => setWizardOpen(true)}
                                    disabled={course.status === "DRAFT"}
                                    aria-label="Assign Course"
                                    title={"Publish course to assign"}
                                >
                                    <Plus className="mr-1 h-4 w-4" />
                                    {t("common.fields.assignCourse")}
                                </Button>
                            </>
                        }
                    />
                    <AssignmentOverview
                        course={course}
                        totalAssigned={assignments.length}
                        completed={completedCount}
                        inProgress={inProgressCount}
                        overdue={overdueCount}
                        completionByOrg={completionByOrg}
                    />
                </TabsContent>
                <TabsContent value="users">
                    <SectionHeader
                        title={t("admin.lms.assignedUsers")}
                        eyebrow={`${assignments.length} assigned`}
                        actions={
                            <Button size="sm" onClick={() => setWizardOpen(true)}>
                                <Plus className="mr-1 h-4 w-4" />
                                {t("common.fields.assignCourse")}
                            </Button>
                        }
                    />
                    <AssignmentUsers
                        assignments={assignments}
                        onAssignmentsChanged={() => {
                            void refetchAssignments();
                            void refetchProgress();
                        }}
                    />
                </TabsContent>
            </Container>
        </Tabs>
    );
}
